'use client';

import React from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { useTranslations } from 'next-intl';

import { HOMEPAGE_MAX_PRODUCTS } from './constants.ts';
import styles from './styles.module.css';

import { HomepageProductCard } from '@/app/[locale]/(home)/components/HomepageProductCard';
import { ExtendedLink } from '@/components/ExtendedLink';
import { ModuleHeader, ModuleHeaderSize } from '@/components/ModuleHeader';
import { ScrollableItemRow } from '@/components/ScrollableItemRow';
import {
  ActivityTrackerEventType,
  HomePageModuleNames,
  ModuleInteractionType,
  moduleOriginParamName,
} from '@/modules/activityTracker/constants.ts';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker.ts';
import { picturePreviewAdapter } from '@/modules/pictures/helpers';
import { getProductCardColumns } from '@/modules/recentlyViewed/helpers.ts';
import { appendParams } from '@/modules/routing/appendParams/appendParams.ts';
import { type PresApiSearchProduct } from '@/modules/search/aliases.ts';
import { getProductSizeName } from '@/modules/sizes/helpers.ts';
import { useCurrentUser } from '@/modules/user/useCurrentUser.ts';

type Props = {
  searchTerm: string;
  products: PresApiSearchProduct[];
};

export function RecentSearchesUi({ searchTerm, products }: Props) {
  const tCommon = useTranslations('common');
  const tHome = useTranslations('home');
  const [, currentUser] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });

  const searchURL = appendParams(
    `/search/?q=${encodeURIComponent(searchTerm)}`,
    [moduleOriginParamName, HomePageModuleNames.RecentFromYourSearch]
  );

  function handleSeeMoreClick() {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MODULE_INTERACTION_ACTION,
      {
        moduleName: HomePageModuleNames.RecentFromYourSearch,
        interactionType: ModuleInteractionType.SeeMoreClicked,
        schemaVersion: '1.0',
      }
    );
  }

  function handleProductClick(
    selectedProductId: number,
    selectedProductName: string,
    selectedProductPosition: number
  ) {
    sendActivityTrackerEvent(ActivityTrackerEventType.MODULE_CLICK_ACTION, {
      moduleName: HomePageModuleNames.RecentFromYourSearch,
      itemClicked: selectedProductName,
      itemId: String(selectedProductId),
      itemPosition: selectedProductPosition,
      schemaVersion: '3.0',
    });
  }

  return (
    <section>
      <ModuleHeader
        title={
          <>
            {tHome('NewForYourSearch')}
            <span className={styles.searchTerm}>&quot;{searchTerm}&quot;</span>
          </>
        }
        size={ModuleHeaderSize.Large}
      >
        <ExtendedLink href={searchURL} onClick={handleSeeMoreClick}>
          <Body variant="sm-regular" className={styles.seeMoreText}>
            {tCommon('SeeMore')}
          </Body>
        </ExtendedLink>
      </ModuleHeader>

      <ScrollableItemRow
        minItemWidth={144}
        itemsPerRow={getProductCardColumns(products.length)}
      >
        {products.slice(0, HOMEPAGE_MAX_PRODUCTS).map((productItem, index) => (
          <li key={productItem.id} className={styles.productListItem}>
            <HomepageProductCard
              id={productItem.id}
              isLiked={productItem.is_liked}
              preview={
                productItem.pictures?.[0]
                  ? picturePreviewAdapter(productItem.pictures[0])
                  : {}
              }
              productHref={`${productItem.slug}?moduleOrigin=${HomePageModuleNames.RecentFromYourSearch}`}
              pictures={productItem.pictures?.map(picturePreviewAdapter)} // HomepageProductCard pictures prop expects a Preview type
              displayPrice={productItem.pricing.display_price}
              hasVideo={!!(productItem.videos && productItem.videos.length > 0)}
              size={
                productItem.sizes &&
                getProductSizeName(
                  productItem.sizes?.map((s) => s.name ?? ''),
                  {
                    multipleSizes: tCommon('MultipleSizes'),
                  }
                )
              }
              handleClick={() =>
                handleProductClick(productItem.id, productItem.slug, index)
              }
              brandName={productItem.brand_name}
            />
          </li>
        ))}
      </ScrollableItemRow>
    </section>
  );
}
